import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import {
  Target, RefreshCw, AlertTriangle, TrendingUp, TrendingDown,
  Clock, Minus, ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';
import { quantLiveSignal, quantPaperClosePosition, getErrorMessage } from '../../lib/api';

/**
 * LiveSignalBadge — card polling 30s con il verdetto strategia+symbol.
 *
 * Verdicts: FLAT | LONG | EXIT_PENDING | STALE
 * Stato 503 (Alpaca non configurato): banner con CTA, no polling.
 *
 * Props:
 *  - strategy: string
 *  - symbol: string
 *  - assetMeta: object|null (con `class` per check US)
 *  - onOpenOrder: callback({strategy, symbol, lastPrice, signalSnapshot})
 *  - onSignalUpdate: callback(signalData|null) — per propagare al parent
 */
const US_CLASSES = new Set([
  'etf_core', 'etf_sector', 'etf_commodity', 'etf_bond', 'etf_volatility',
  'etf_other', 'stock',
]);

// Formatta numero con tabular-nums, max 2 decimali
const fmtPrice = (v) => (v == null ? '—' : `$${Number(v).toFixed(2)}`);

// Config visual per ogni verdict
const VERDICT_CONFIG = {
  FLAT: {
    label: 'FLAT',
    sublabel: '(no posizione)',
    ring: 'ring-border',
    badgeBg: 'bg-muted/60 text-muted-foreground',
    icon: <Minus className="w-5 h-5" />,
  },
  LONG: {
    label: 'LONG',
    sublabel: '(in posizione)',
    ring: 'ring-emerald-500/60',
    badgeBg: 'bg-emerald-600 text-white',
    icon: <TrendingUp className="w-5 h-5" />,
  },
  LONG_ENTRY: {
    // verdict=LONG ma in_position=false: entry da aprire
    label: 'ENTRY!',
    sublabel: '(segnale fresh)',
    ring: 'ring-amber-400/70',
    badgeBg: 'bg-amber-500 text-white',
    icon: <TrendingUp className="w-5 h-5" />,
  },
  EXIT_PENDING: {
    label: 'EXIT pending',
    sublabel: '',
    ring: 'ring-orange-400/70',
    badgeBg: 'bg-orange-500 text-white',
    icon: <TrendingDown className="w-5 h-5" />,
  },
  STALE: {
    label: 'STALE',
    sublabel: '(dati vecchi)',
    ring: 'ring-border',
    badgeBg: 'bg-muted/60 text-muted-foreground',
    icon: <Clock className="w-5 h-5" />,
  },
};

const LiveSignalBadge = ({ strategy, symbol, assetMeta, onOpenOrder, onSignalUpdate }) => {
  const [signal, setSignal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [providerMissing, setProviderMissing] = useState(false);
  const [closingPosition, setClosingPosition] = useState(false);
  const timerRef = useRef(null);

  const isUSAsset = !assetMeta?.class || US_CLASSES.has(assetMeta.class);

  const fetchSignal = useCallback(async () => {
    if (!strategy || !symbol || !isUSAsset) return;
    setLoading(true);
    setError(null);
    try {
      const r = await quantLiveSignal(strategy, symbol);
      setSignal(r.data);
      setProviderMissing(false);
      onSignalUpdate?.(r.data);
    } catch (e) {
      if (e.response?.status === 503) {
        setProviderMissing(true);
        onSignalUpdate?.(null);
      } else {
        setError(getErrorMessage(e, 'Errore segnale live'));
        onSignalUpdate?.(null);
      }
      setSignal(null);
    } finally {
      setLoading(false);
    }
  }, [strategy, symbol, isUSAsset, onSignalUpdate]);

  useEffect(() => {
    fetchSignal();
    // Poll ogni 30s — stesso pattern di LiveQuoteCard
    timerRef.current = setInterval(fetchSignal, 30000);
    return () => {
      clearInterval(timerRef.current);
      // Notifica il parent che il badge è smontato
      onSignalUpdate?.(null);
    };
  }, [fetchSignal, onSignalUpdate]);

  // Non-US: nessun polling disponibile
  if (!isUSAsset) {
    return (
      <Card className="border-dashed">
        <CardContent className="py-3 text-xs text-muted-foreground flex items-center gap-2">
          <Target className="w-3.5 h-3.5" />
          Segnale live non disponibile per <code className="font-mono">{symbol}</code> —
          Alpaca free tier copre solo US equities/ETF.
        </CardContent>
      </Card>
    );
  }

  // Provider mancante: banner 503
  if (providerMissing) {
    return (
      <Card className="border-amber-500/30 bg-amber-50/40 dark:bg-amber-950/20">
        <CardContent className="py-3 text-xs flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-medium">Alpaca non configurato — segnale live non disponibile</p>
            <p className="text-muted-foreground mt-0.5">
              Aggiungi il provider Alpaca per attivare live mode.{' '}
              <a href="/admin/market-providers" className="text-primary hover:underline">
                Configura qui
              </a>
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Determina la configurazione visual
  const resolveVerdict = (sig) => {
    if (!sig) return null;
    if (sig.verdict === 'STALE') return 'STALE';
    if (sig.verdict === 'FLAT') return 'FLAT';
    if (sig.verdict === 'EXIT_PENDING') return 'EXIT_PENDING';
    if (sig.verdict === 'LONG') {
      return sig.in_position ? 'LONG' : 'LONG_ENTRY';
    }
    return 'FLAT';
  };

  const verdictKey = resolveVerdict(signal);
  const cfg = verdictKey ? VERDICT_CONFIG[verdictKey] : null;

  // Indicatori: mostra solo quelli non-null
  const indicators = signal?.indicators
    ? Object.entries(signal.indicators).filter(([, v]) => v != null)
    : [];

  // Freschezza dati
  const freshLabel = signal
    ? signal.data_freshness === 'fresh' ? 'fresh' : signal.data_freshness || '—'
    : null;

  // Handler chiusura posizione
  const handleClosePosition = async () => {
    if (!symbol) return;
    setClosingPosition(true);
    try {
      await quantPaperClosePosition(symbol);
      toast.success(`Posizione ${symbol} chiusa`, { description: 'Ordine market sell inviato ad Alpaca paper.' });
      // Refetch immediato per aggiornare verdict
      await fetchSignal();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Errore chiusura posizione'));
    } finally {
      setClosingPosition(false);
    }
  };

  // Handler apertura ordine paper
  const handleOpenOrder = () => {
    if (!signal) return;
    onOpenOrder?.({
      strategy,
      symbol,
      lastPrice: signal.last_bar?.close ?? null,
      signalSnapshot: signal,
    });
  };

  return (
    <Card className={`ring-2 ${cfg?.ring || 'ring-border'} transition-all duration-300`}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center justify-between gap-2">
          <span className="flex items-center gap-2 min-w-0">
            <Target className={`w-4 h-4 shrink-0 ${loading ? 'animate-pulse text-primary' : 'text-primary'}`} />
            <span className="truncate">
              Segnale Live · <code className="font-mono text-[11px]">{strategy}</code>
              {' · '}<code className="font-mono">{symbol}</code>
            </span>
          </span>
          <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={fetchSignal} disabled={loading} aria-label="Aggiorna segnale">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </CardTitle>
      </CardHeader>

      <CardContent className="pt-0">
        {error ? (
          <div className="text-xs text-red-500 flex items-center gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
            {error}
          </div>
        ) : signal && cfg ? (
          <div className="space-y-3">
            {/* Riga principale: badge verdict + trigger levels + last bar */}
            <div className="flex flex-col sm:flex-row sm:items-start gap-3">
              {/* Badge verdict */}
              <div className={`rounded-lg px-4 py-3 flex flex-col items-center justify-center min-w-[130px] ${cfg.badgeBg}`}>
                <span className="flex items-center gap-1.5 font-bold text-lg leading-tight">
                  {cfg.icon}
                  {cfg.label}
                </span>
                {cfg.sublabel && (
                  <span className="text-[10px] opacity-80 mt-0.5">{cfg.sublabel}</span>
                )}
              </div>

              {/* Trigger levels + last bar */}
              <div className="flex-1 grid grid-cols-1 gap-0.5 text-sm">
                {signal.trigger_long != null && (
                  <div className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
                    <TrendingUp className="w-3.5 h-3.5 shrink-0" />
                    <span className="text-muted-foreground text-xs">Trigger LONG:</span>
                    <span className="font-mono tabular-nums font-medium">{fmtPrice(signal.trigger_long)}</span>
                  </div>
                )}
                {signal.trigger_exit != null && (
                  <div className="flex items-center gap-1 text-red-500 dark:text-red-400">
                    <TrendingDown className="w-3.5 h-3.5 shrink-0" />
                    <span className="text-muted-foreground text-xs">Trigger EXIT:</span>
                    <span className="font-mono tabular-nums font-medium">{fmtPrice(signal.trigger_exit)}</span>
                  </div>
                )}
                {signal.last_bar?.close != null && (
                  <div className="flex items-center gap-1 text-muted-foreground">
                    <ChevronRight className="w-3.5 h-3.5 shrink-0" />
                    <span className="text-xs">Last bar:</span>
                    <span className="font-mono tabular-nums text-foreground">{fmtPrice(signal.last_bar.close)}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Indicatori: solo quelli != null */}
            {indicators.length > 0 && (
              <p className="text-[11px] text-muted-foreground font-mono tabular-nums leading-relaxed">
                Indicatori: {indicators.map(([k, v]) => `${k}=${typeof v === 'number' ? v.toFixed(2) : v}`).join(' · ')}
              </p>
            )}

            {/* Banner EXIT_PENDING */}
            {verdictKey === 'EXIT_PENDING' && (
              <div className="flex items-start gap-2 bg-orange-50 dark:bg-orange-950/20 border border-orange-200 dark:border-orange-900 rounded-md px-3 py-2 text-xs text-orange-700 dark:text-orange-300">
                <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs text-amber-700 dark:text-amber-300">
                    Segnale di uscita rilevato.
                  </p>
                  <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
                    Considera la chiusura manuale della posizione.
                    <strong className="block mt-1">Questa app NON chiude posizioni in automatico.</strong>
                  </p>
                </div>
              </div>
            )}

            {/* Banner dati STALE */}
            {verdictKey === 'STALE' && (
              <div className="flex items-start gap-2 bg-muted/40 border rounded-md px-3 py-2 text-xs text-muted-foreground">
                <Clock className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                Dati vecchi — verifica la connessione al provider dati.
              </div>
            )}

            {/* CTA + metadata freschezza */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pt-1 border-t">
              {/* Bottone azione principale */}
              <div>
                {verdictKey === 'LONG_ENTRY' && (
                  <Button size="sm" className="bg-amber-500 hover:bg-amber-600 text-white" onClick={handleOpenOrder}>
                    <TrendingUp className="w-3.5 h-3.5 mr-1.5" />
                    Apri posizione paper
                  </Button>
                )}
                {verdictKey === 'LONG' && (
                  <Button size="sm" variant="destructive" onClick={handleClosePosition} disabled={closingPosition}>
                    {closingPosition ? (
                      <RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                    ) : (
                      <TrendingDown className="w-3.5 h-3.5 mr-1.5" />
                    )}
                    Chiudi posizione
                  </Button>
                )}
                {verdictKey === 'FLAT' && (
                  <Button size="sm" variant="outline" disabled title="Aspetta trigger LONG">
                    <TrendingUp className="w-3.5 h-3.5 mr-1.5 opacity-40" />
                    Apri posizione paper
                  </Button>
                )}
                {verdictKey === 'EXIT_PENDING' && (
                  <Button size="sm" variant="destructive" onClick={handleClosePosition} disabled={closingPosition}>
                    {closingPosition ? (
                      <RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                    ) : (
                      <TrendingDown className="w-3.5 h-3.5 mr-1.5" />
                    )}
                    Chiudi posizione
                  </Button>
                )}
              </div>

              {/* Freschezza + frequenza poll */}
              <p className="text-[9px] text-muted-foreground font-mono">
                {freshLabel && <>data: {freshLabel} · </>}upd 30s
              </p>
            </div>
          </div>
        ) : loading ? (
          <div className="h-20 bg-muted/40 animate-pulse rounded" />
        ) : null}
      </CardContent>
    </Card>
  );
};

export default LiveSignalBadge;
